import {
  Button,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { GeoJSONFeatureId, MapLibreEvent } from "maplibre-gl";
import { default as React, useEffect, useRef, useState } from "react";
import Map, { MapRef } from "react-map-gl/maplibre";
import * as turf from "@turf/turf";
import { Feature, FeatureCollection, LineString, Point } from "geojson";
import maplibregl from "maplibre-gl";
import carImage from "../../../assets/auto.png";
import planeImage from "../../../assets/plane.png";
import { bearing, point } from "@turf/turf";

const mapStyleJsons = {
  positron: "https://tiles.openfreemap.org/styles/positron",
};

export default function TravelAnimationSection(props: {
  uniqueMapId: string;
  origin: number[];
  destination: number[];
  autoPlay?: boolean;
  autoPlayDelayMs?: number;
  interactive?: boolean;
  showRouteLine?: boolean;
  projection?: "globe" | "mercator" | undefined;
  zoom?: number;
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const colorScheme = useComputedColorScheme();
  const mapRef = useRef<MapRef>(null);
  const initializedRef = useRef(false);
  const [animationData, setAnimationData] = useState(null);

  const defaultProps = {
    zoom: 3,
  };
  const [config, setConfig] = useState({ ...defaultProps, ...props });
  const [animationReqId, setAnimationReqId] = useState<number | null>(null);

  useEffect(() => {
    setConfig({ ...defaultProps, ...props });

    //mapRef.current?.getMap().setZoom(config.zoom);
  }, [props, props.uniqueMapId]);

   useEffect(() => {
    return () => {
      if (animationReqId) {
        cancelAnimationFrame(animationReqId);
      }
    };
  }, []);

  const onLoadMap = () => {
    const { map, point, route, steps } = initializeTravelAnimation();

    if (config.autoPlay) {
      if (config.autoPlayDelayMs) {
        setTimeout(() => {
          triggerTravelAnimation(map, point, route, steps);
        }, config.autoPlayDelayMs);
        return;
      }
      triggerTravelAnimation(map, point, route, steps);
    }
  };

  const initializeTravelAnimation = () => {
    const map = mapRef.current?.getMap();

    if (!mapRef?.current) return;
    if (!map || initializedRef.current) return;

    const { origin, destination } = props;

    // A simple line from origin to destination.
    const route = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [origin, destination],
          },
        },
      ],
    };

    // A single point that animates along the route.
    // Coordinates are initially set to origin.
    const point: any = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Point",
            coordinates: origin,
          },
        },
      ],
    };

    // Calculate the distance in kilometers between route start/end point.
    const lineDistance = turf.distance(
      route.features[0].geometry.coordinates[0],
      route.features[0].geometry.coordinates[1],
      { units: "kilometers" }
    );

    const arc = [];

    // Number of steps to use in the arc and animation, more steps means
    // a smoother arc and animation, but too many steps will result in a
    // low frame rate
    const steps = 1000;

    // Define line
    const line = turf.lineString(route.features[0].geometry.coordinates, {
      units: "kilometers",
    });

    // Draw an arc between the `origin` & `destination` of the two points
    for (let i = 0; i < lineDistance; i += lineDistance / steps) {
      const segment = turf.along(line, i, { units: "kilometers" });
      arc.push(segment.geometry.coordinates);
    }

    // Update the route with calculated arc coordinates
    route.features[0].geometry.coordinates = arc;

    // Add a source and layer displaying a point which will be animated in a circle.
    map.addSource("route", {
      type: "geojson",
      data: route as any,
    });

    /* map.addSource("point", {
      type: "geojson",
      data: point as any,
    });*/

    if (config.showRouteLine) {
      map.addLayer({
        id: "route",
        source: "route",
        type: "line",
        paint: {
          "line-width": 2,
          "line-color": "#007cbf",
        },
      });
    }

    /*map.addLayer({
      id: "point",
      source: "point",
      type: "symbol",
      layout: {
        "icon-image": "airport",
        "icon-rotate": ["get", "bearing"],
        "icon-rotation-alignment": "map",
        "icon-overlap": "always",
        "icon-ignore-placement": true,
      },
    });*/

    loadTransportationIcons(map, point, route, "plane");

    map.setCenter({
      lat: origin[1],
      lng: origin[0],
    });

    setAnimationData({ map, point, route, steps });

    return { map, point, route, steps };
  };

  const loadTransportationIcons = (
    map: any,
    point: any,
    route: any,
    icon: "car" | "plane"
  ) => {
    if (icon === "car") {
      const routeFirstPoint = route.features[0].geometry.coordinates[0];
      const routeLastPoint =
        route.features[0].geometry.coordinates[
          route.features[0].geometry.coordinates.length - 1
        ];

      const isTrajectoryRight =
        getDirectionTurf(routeFirstPoint, routeLastPoint) === "right";

      map.loadImage(carImage).then((image) => {
        const htmlImage = new Image();
        htmlImage.src = carImage;
        htmlImage.onload = () => {
          map.addImage("auto", htmlImage);
          map.addSource("point", {
            type: "geojson",
            data: point as any,
          });

          map.addLayer({
            id: "points",
            type: "symbol",
            source: "point", // reference the data source
            layout: {
              "icon-image": "auto", // reference the image
              "icon-size": 0.5,
              "icon-rotate": ["get", "rotate"],
              "icon-rotation-alignment": "map",
              "icon-overlap": "always",
              "icon-ignore-placement": true,
            },
          });
        };
      });
    }

    if (icon === "plane") {
      map.loadImage(planeImage).then((image) => {
        const htmlImage = new Image();
        htmlImage.src = planeImage;
        htmlImage.onload = () => {
          map.addImage("plane", htmlImage);
          map.addSource("point", {
            type: "geojson",
            data: point as any,
          });

          map.addLayer({
            id: "points",
            type: "symbol",
            source: "point", // reference the data source
            layout: {
              "icon-image": "plane", // reference the image
              "icon-size": 0.4,
              "icon-rotate": ["get", "bearing"],
              "icon-rotation-alignment": "map",
              "icon-overlap": "always",
              "icon-ignore-placement": true,
            },
          });
        };
      });
    }
  };

  function getDirectionTurf(
    pointA: [number, number],
    pointB: [number, number]
  ): "right" | "left" | "none" {
    const brg = bearing(point(pointA), point(pointB)); // bearing in degrees from -180 to 180
    if (Math.abs(brg) < 1e-6) return "none";
    return brg > 0 ? "right" : "left";
  }

  const triggerTravelAnimation = (
    mapP = null,
    pointP = null,
    routeP = null,
    stepsP = null
  ) => {
    let map, point, route, steps;
    if (!animationData || (!!mapP && !!pointP && !!routeP && !!stepsP)) {
      map = mapP;
      point = pointP;
      route = routeP;
      steps = stepsP;
      console.log(
        "triggerTravelAnimation with default params",
        mapP,
        pointP,
        routeP,
        stepsP
      );
    } else {
      console.log("triggerTravelAnimation with animationData", animationData);
      map = mapP;
      point = pointP;
      route = routeP;
      steps = stepsP;
      ({ map, point, route, steps } = animationData);
    }

    // Used to increment the value of the point measurement against the route.
    let counter = 0;

    function animate() {
      if (counter === steps - 1) return;
      if (!map) return;
      if (!map.style) return;

      // Update point geometry to a new position based on counter denoting
      // the index to access the arc.
      point.features[0].geometry.coordinates =
        route.features[0].geometry.coordinates[counter];

      // Calculate the bearing to ensure the icon is rotated to match the route arc
      // The bearing is calculate between the current point and the next point, except
      // at the end of the arc use the previous point and the current point
      // TODO: fix typing
      (point.features[0].properties as any).bearing = turf.bearing(
        turf.point(
          route.features[0].geometry.coordinates[
            counter >= steps ? counter - 1 : counter
          ]
        ),
        turf.point(
          route.features[0].geometry.coordinates[
            counter >= steps ? counter : counter + 1
          ]
        )
      );

      mapRef?.current?.setCenter({
        lat: point.features[0].geometry.coordinates[1],
        lng: point.features[0].geometry.coordinates[0],
      });

      // Update the source with this new data.
      (map?.getSource("point") as maplibregl.GeoJSONSource)?.setData(point);

      counter = counter + 1;

      // Request the next frame of animation so long the end has not been reached.
      if (counter < steps) {
        setAnimationReqId(requestAnimationFrame(animate));
      }
    }

    // Start the animation.
    animate();
  };

  return (
    <div className="flex flex-col w-full h-full gap-4">
      <div className="w-full h-full rounded-lg border border-grey-800 overflow-hidden">
        <Map
          id={props.uniqueMapId}
          reuseMaps={false /** TODO: enable reuseMaps */}
          initialViewState={{
            longitude: config.origin[0],
            latitude: config.origin[1],
            zoom: config.zoom,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle={mapStyleJsons.positron}
          onLoad={(event: MapLibreEvent) => {
            onLoadMap();
          }}
          interactive={!!config.interactive}
          projection={config.projection ?? "mercator"}
          attributionControl={false}
          ref={mapRef}
        ></Map>
      </div>
      {/* <Button onClick={triggerTravelAnimation}>Travel</Button> */}
    </div>
  );
}
